import React, { useEffect, useMemo, useRef } from 'react';
import { Drawer, DrawerContent } from '@/components/ui/drawer';
import { deriveSecondary } from '../components/GenieChatInputWeb';
import { GenieTextChatOverlayWeb } from '../components/GenieTextChatOverlayWeb';
import { TranscriptMessage } from '../components/TranscriptPanel';
import type { ChatAppearanceProps } from '@/types/voice/chat-appearance';

const hexToRgb = (hex: string): [number, number, number] => {
  const m = (hex || "").replace("#", "");
  if (m.length !== 6) return [78, 156, 255];
  return [
    parseInt(m.substring(0, 2), 16) || 0,
    parseInt(m.substring(2, 4), 16) || 0,
    parseInt(m.substring(4, 6), 16) || 0,
  ];
};

interface MobileTranscriptDrawerProps {
  chatAppearance?: ChatAppearanceProps;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  brandColor: string;
  secondaryColor?: string | null;
  agentName?: string;
  messages: TranscriptMessage[];
  isConnected?: boolean;
  isListening?: boolean;
  isConnecting?: boolean;
  isSpeaking?: boolean;
  onSendMessage?: (text: string) => void;
  isAllAudioMuted?: boolean;
  onToggleAllAudioMute?: () => void;
  // Text-only mode props
  isTextOnlyMode?: boolean;
  inlineMessage?: string;
  onChangeInlineMessage?: (value: string) => void;
  onSend?: () => void;
  startedFromText?: boolean;
  onEndChat?: () => void;
  attachments?: Array<{
    id: string;
    previewUrl: string;
    status: "analyzing" | "ready" | "error";
    analysisText?: string;
  }>;
  onRemoveAttachment?: (id: string) => void;
  onOpenFilePicker?: () => void;
  onUpdateAttachmentAnalysis?: (id: string, text: string) => void;
  imageUploadPendingCount?: number;
  imageUploadFailedCount?: number;
  imageUploadSaving?: boolean;
  onRetryImageUploads?: () => void;
  /**
   * Voice controls (mic / volume / pause) to render in the drawer header's
   * left slot so the user keeps mic + volume access while reading the chat.
   */
  headerVoiceControls?: React.ReactNode;
  /** End the active voice conversation from the chat header. */
  onEndConversation?: () => void;
  /**
   * True when the drawer is force-locked open (post-conversation feedback
   * view). When true, the chat overlay hides its close X — clicking it just
   * bounces back, which traps the user.
   */
  isLockedOpen?: boolean;
  /** The in-conversation capture moment, forwarded to the chat overlay's message stream. */
  inlineCaptureSlot?: React.ReactNode;
}

export const MobileTranscriptDrawer = ({
  chatAppearance,
  open,
  onOpenChange,
  brandColor,
  secondaryColor,
  agentName,
  messages,
  isConnected = false,
  isListening = false,
  isConnecting = false,
  isSpeaking = false,
  onSendMessage,
  isAllAudioMuted = false,
  onToggleAllAudioMute,
  isTextOnlyMode = false,
  inlineMessage = "",
  onChangeInlineMessage,
  onSend,
  startedFromText = false,
  onEndChat,
  attachments = [],
  onRemoveAttachment,
  onOpenFilePicker,
  onUpdateAttachmentAnalysis,
  imageUploadPendingCount,
  imageUploadFailedCount,
  imageUploadSaving,
  onRetryImageUploads,
  headerVoiceControls,
  onEndConversation,
  isLockedOpen = false,
  inlineCaptureSlot,
}: MobileTranscriptDrawerProps) => {
  const contentRef = useRef<HTMLDivElement | null>(null);
  const shouldLockDrawer = Boolean(startedFromText && isConnected);
  const canRenderChatOverlay = Boolean(onSend && onChangeInlineMessage);

  // Resolve agent brand colors so the halo speaks the tenant's brand,
  // not Help Genie's. Derive a secondary when the agent doesn't ship one.
  const haloColors = useMemo(() => {
    const start = brandColor || "#4E9CFF";
    const end = secondaryColor || deriveSecondary(start);
    const [r1, g1, b1] = hexToRgb(start);
    const [r2, g2, b2] = hexToRgb(end);
    return {
      gradient: `linear-gradient(90deg, ${start} 0%, ${end} 100%)`,
      primary: (a: number) => `rgba(${r1}, ${g1}, ${b1}, ${a})`,
      secondary: (a: number) => `rgba(${r2}, ${g2}, ${b2}, ${a})`,
    };
  }, [brandColor, secondaryColor]);

  // Brand halo tuned to voice state — gradient ridge along the drawer's top edge,
  // a wider mid-glow, and a deep outer aura that all intensify when the agent
  // is speaking. Lets the drawer feel like an extension of the genie orb.
  const halo = isSpeaking
    ? {
        lineOpacity: 1,
        lineHeight: 3,
        shadow: [
          `0 -3px 0 0 ${haloColors.primary(1)}`,
          `0 -6px 14px -2px ${haloColors.primary(0.85)}`,
          `0 -18px 44px -6px ${haloColors.primary(0.7)}`,
          `0 -36px 90px -12px ${haloColors.secondary(0.65)}`,
          `0 -60px 140px -30px ${haloColors.secondary(0.5)}`,
        ].join(", "),
      }
    : isListening
    ? {
        lineOpacity: 0.92,
        lineHeight: 2.5,
        shadow: [
          `0 -2.5px 0 0 ${haloColors.primary(0.85)}`,
          `0 -5px 12px -2px ${haloColors.primary(0.6)}`,
          `0 -16px 38px -8px ${haloColors.primary(0.5)}`,
          `0 -30px 72px -16px ${haloColors.secondary(0.45)}`,
          `0 -54px 120px -34px ${haloColors.secondary(0.35)}`,
        ].join(", "),
      }
    : isConnecting
    ? {
        lineOpacity: 0.85,
        lineHeight: 2.5,
        shadow: [
          `0 -2px 0 0 ${haloColors.primary(0.75)}`,
          `0 -6px 16px -4px ${haloColors.primary(0.5)}`,
          `0 -18px 42px -10px ${haloColors.primary(0.4)}`,
          `0 -32px 78px -20px ${haloColors.secondary(0.36)}`,
        ].join(", "),
      }
    : {
        lineOpacity: 0.7,
        lineHeight: 2,
        shadow: [
          `0 -1.5px 0 0 ${haloColors.primary(0.55)}`,
          `0 -4px 14px -4px ${haloColors.primary(0.35)}`,
          `0 -14px 36px -10px ${haloColors.primary(0.28)}`,
          `0 -28px 64px -20px ${haloColors.secondary(0.24)}`,
        ].join(", "),
      };

  useEffect(() => {
    // prevent behind scroll when drawer open
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  // Voice mode transcript drawer: if the drawer isn't open, render nothing.
  if (!open) return null;

  // Voice mode transcript drawer (keeps vaul behavior)
  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      shouldScaleBackground={false}
      {...(shouldLockDrawer
        ? {
          dismissible: false,
          handleOnly: true,
          modal: true,
          fixed: true,
        }
        : {})}
    >
      <DrawerContent
        ref={contentRef}
        className={
          shouldLockDrawer
            ? "flex flex-col p-0 z-[10000] rounded-t-none"
            : "h-[95dvh] flex flex-col p-0 overflow-hidden rounded-t-[24px] border-transparent"
        }
        style={{
          display: "flex",
          flexDirection: "column",
          height: shouldLockDrawer ? "100%" : undefined,
          minHeight: 0,
          zIndex: 9999,
          // Avoid double-applying safe area: we add a single bottom spacer below.
          paddingBottom: 0,
          boxShadow: shouldLockDrawer ? undefined : halo.shadow,
          transition: "box-shadow 350ms ease",
          ...(shouldLockDrawer ? { top: 0, bottom: 0, maxHeight: "100dvh" } : {}),
        }}
      >
        {!shouldLockDrawer && (
          <div
            aria-hidden
            className="pointer-events-none absolute left-0 right-0 top-0 z-10"
            style={{
              height: halo.lineHeight,
              background: haloColors.gradient,
              opacity: halo.lineOpacity,
              transition: "opacity 350ms ease, height 350ms ease",
              borderTopLeftRadius: 24,
              borderTopRightRadius: 24,
              filter: isSpeaking
                ? `drop-shadow(0 0 6px ${haloColors.primary(0.7)})`
                : undefined,
            }}
          />
        )}
        {canRenderChatOverlay ? (
          <>
            <div
              style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0, overflow: "hidden" }}
            >
              <GenieTextChatOverlayWeb
                {...chatAppearance}
                brandColor={brandColor}
                agentName={agentName}
                messages={messages}
                inlineMessage={inlineMessage}
                onChangeInlineMessage={onChangeInlineMessage}
                onSend={onSend}
                isConnecting={isConnecting}
                isSpeaking={isSpeaking}
                isTextOnlyMode={isTextOnlyMode}
                isAllAudioMuted={isAllAudioMuted}
                onToggleAllAudioMute={onToggleAllAudioMute || (() => { })}
                startedFromText={startedFromText}
                onCloseOverlay={() => {
                  if (!shouldLockDrawer) {
                    onOpenChange(false);
                  }
                }}
                onEndChat={onEndChat || (() => { })}
                attachments={attachments}
                onRemoveAttachment={onRemoveAttachment}
                onOpenFilePicker={onOpenFilePicker}
                onUpdateAttachmentAnalysis={onUpdateAttachmentAnalysis}
                imageUploadPendingCount={imageUploadPendingCount}
                imageUploadFailedCount={imageUploadFailedCount}
                imageUploadSaving={imageUploadSaving}
                onRetryImageUploads={onRetryImageUploads}
                inDrawer={true}
                headerLeftSlot={headerVoiceControls}
                voiceConnected={isConnected}
                onEndConversation={onEndConversation}
                hideClose={isLockedOpen}
                inlineCaptureSlot={inlineCaptureSlot}
              />
            </div>
            <div style={{ height: "max(8px, env(safe-area-inset-bottom))" }} />
          </>
        ) : (
          <>
            <div style={{ height: "max(8px, env(safe-area-inset-bottom))" }} />
          </>
        )}
      </DrawerContent>
    </Drawer>
  );
};

export default MobileTranscriptDrawer;
